import React, { useState, useEffect, useCallback, useRef } from 'react';
import Sidebar from './Sidebar';
import api from '../utils/axios';
import { useNavigate, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const AnalyticsDetails = () => {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [, setError] = useState(null);
    const [filters, setFilters] = useState({
        startDate: '',
        endDate: '',
        mode: 'all',
        userId: '',
        tenantId: ''
    });
    const [selectedUser, setSelectedUser] = useState(null);
    const [conversation, setConversation] = useState([]);
    const [loadingConversation, setLoadingConversation] = useState(false);
    const [highlightedMessageId, setHighlightedMessageId] = useState(null);
    const highlightedMessageRef = useRef(null);
    const [tenantInfo, setTenantInfo] = useState(null);

    const location = useLocation();

    const [isInitialized, setIsInitialized] = useState(false);

    const fetchUsers = useCallback(async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (filters.startDate) params.append('startDate', filters.startDate);
            if (filters.endDate) params.append('endDate', filters.endDate);
            if (filters.mode !== 'all') params.append('mode', filters.mode);
            if (filters.userId) params.append('userId', filters.userId);
            if (filters.tenantId) params.append('tenantId', filters.tenantId);

            const response = await api.get(`/api/analytics/users?${params.toString()}`);
            setUsers(response.data);
            setLoading(false);
        } catch (err) {
            console.error('Error fetching users:', err);
            setError('Failed to load user data');
            setLoading(false);
        }
    }, [filters]);

    const fetchConversation = useCallback(async (userId) => {
        try {
            setLoadingConversation(true);
            setTenantInfo(null);
            const params = new URLSearchParams();
            if (filters.startDate) params.append('startDate', filters.startDate);
            if (filters.endDate) params.append('endDate', filters.endDate);

            const [convResponse, tenantResponse] = await Promise.all([
                api.get(`/api/analytics/conversations/${userId}?${params.toString()}`),
                api.get(`/api/analytics/tenant/${userId}`),
            ]);
            setConversation(convResponse.data);
            setSelectedUser(userId);
            setTenantInfo(tenantResponse.data.tenant || null);
            setLoadingConversation(false);
        } catch (err) {
            console.error('Error fetching conversation:', err);
            setLoadingConversation(false);
        }
    }, [filters]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const userIdParam = params.get('userId');
        const startDateParam = params.get('startDate');
        const endDateParam = params.get('endDate');
        const modeParam = params.get('mode');
        const messageIdParam = params.get('messageId');

        // Set initial filters from URL params
        const newFilters = {
            userId: userIdParam || '',
            startDate: startDateParam || '',
            endDate: endDateParam || '',
            mode: modeParam || 'all'
        };
        setFilters(newFilters);
        setIsInitialized(true);

        // Set highlighted message ID if provided
        if (messageIdParam) {
            setHighlightedMessageId(messageIdParam);
        }

        // If specific user is requested in URL, load their conversation immediately
        if (userIdParam) {
            const fetchParams = new URLSearchParams();
            if (startDateParam) fetchParams.append('startDate', startDateParam);
            if (endDateParam) fetchParams.append('endDate', endDateParam);

            setLoadingConversation(true);
            setTenantInfo(null);
            Promise.all([
                api.get(`/api/analytics/conversations/${userIdParam}?${fetchParams.toString()}`),
                api.get(`/api/analytics/tenant/${userIdParam}`),
            ])
                .then(([convResponse, tenantResponse]) => {
                    setConversation(convResponse.data);
                    setSelectedUser(userIdParam);
                    setTenantInfo(tenantResponse.data.tenant || null);
                    setLoadingConversation(false);
                })
                .catch(err => {
                    console.error('Error fetching conversation:', err);
                    setLoadingConversation(false);
                });
        }
    }, [location.search]);

    useEffect(() => {
        // Only fetch users when filters change AND we have initialized from URL
        if (isInitialized) {
            fetchUsers();
        }
    }, [filters, isInitialized, fetchUsers]);

    // Auto-scroll to highlighted message
    useEffect(() => {
        if (highlightedMessageRef.current && conversation.length > 0) {
            setTimeout(() => {
                highlightedMessageRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
            }, 300);
        }
    }, [conversation, highlightedMessageId]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}`;
    };

    const handleShare = () => {
        const params = new URLSearchParams();
        params.append('userId', selectedUser);
        if (filters.startDate) params.append('startDate', filters.startDate);
        if (filters.endDate) params.append('endDate', filters.endDate);
        if (filters.mode !== 'all') params.append('mode', filters.mode);

        const shareUrl = `${window.location.origin}/analytics/details?${params.toString()}`;
        navigator.clipboard.writeText(shareUrl);
        alert('Link copied to clipboard!');
    };

    return (
        <div className="flex h-screen bg-gray-50">
            <Sidebar />
            <div className="flex-1 overflow-auto">
                <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
                    {/* Header */}
                    <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-4">
                            <button
                                type="button"
                                onClick={() => navigate('/analytics')}
                                className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                            >
                                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                </svg>
                            </button>
                            <h1 className="text-2xl font-bold text-gray-900">Detailed Analytics</h1>
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-wrap gap-4 items-end">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                            <input
                                type="date"
                                name="startDate"
                                value={filters.startDate}
                                onChange={handleFilterChange}
                                className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                            <input
                                type="date"
                                name="endDate"
                                value={filters.endDate}
                                onChange={handleFilterChange}
                                className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Mode</label>
                            <select
                                name="mode"
                                value={filters.mode}
                                onChange={handleFilterChange}
                                className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black min-w-[150px]"
                            >
                                <option value="all">All Modes</option>
                                <option value="guide">Guide IA</option>
                                <option value="agent">Agent IA</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">User ID</label>
                            <input
                                type="text"
                                name="userId"
                                value={filters.userId}
                                onChange={handleFilterChange}
                                placeholder="Search by User ID"
                                className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Pharmacie (Tenant ID)</label>
                            <div className="flex items-center gap-1">
                                <svg className="w-4 h-4 text-indigo-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                                <input
                                    type="text"
                                    name="tenantId"
                                    value={filters.tenantId}
                                    onChange={handleFilterChange}
                                    placeholder="Filtrer par Tenant ID"
                                    className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                        </div>
                    </div>

                    {/* User List Table */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User ID</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Modes Used</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Messages</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Active</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {loading ? (
                                        <tr>
                                            <td colSpan="5" className="px-6 py-4 text-center">
                                                <div className="flex justify-center">
                                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-black"></div>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : users.length === 0 ? (
                                        <tr>
                                            <td colSpan="5" className="px-6 py-4 text-center text-gray-500">No users found matching filters</td>
                                        </tr>
                                    ) : (
                                        users.map((user) => (
                                            <tr key={user.userId} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    {user.userId.substring(0, 8)}...
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    <div className="flex gap-2">
                                                        {user.modes.map(mode => (
                                                            <span key={mode} className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${mode === 'guide' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                                                                }`}>
                                                                {mode === 'guide' ? 'Guide' : 'Agent'}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {user.messageCount}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {formatDate(user.lastActive)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                    <button
                                                        type="button"
                                                        onClick={() => fetchConversation(user.userId)}
                                                        className="text-indigo-600 hover:text-indigo-900"
                                                    >
                                                        View Chat
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* Conversation Modal */}
            {selectedUser && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
                        <div className="p-6 border-b border-gray-200 flex justify-between items-start">
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">
                                    Conversation History
                                    <span className="ml-2 text-sm font-normal text-gray-500">User: {selectedUser}</span>
                                </h2>
                                {tenantInfo && (
                                    <div className="mt-2 flex flex-wrap items-center gap-3">
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs font-semibold">
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                            </svg>
                                            Tenant #{tenantInfo.tenant_id}
                                        </span>
                                        <span className="text-sm font-semibold text-gray-800">{tenantInfo.company_name}</span>
                                        {tenantInfo.pharmacist_name && (
                                            <span className="text-sm text-gray-500">{tenantInfo.pharmacist_name}</span>
                                        )}
                                        {tenantInfo.city && (
                                            <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                </svg>
                                                {tenantInfo.city}
                                            </span>
                                        )}
                                        {tenantInfo.company_email && (
                                            <span className="text-xs text-gray-400">{tenantInfo.company_email}</span>
                                        )}
                                        {tenantInfo.company_phone && (
                                            <span className="text-xs text-gray-400">{tenantInfo.company_phone}</span>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                                <button
                                    type="button"
                                    onClick={handleShare}
                                    className="p-2 text-gray-500 hover:text-indigo-600 transition-colors"
                                    title="Share Conversation"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                                    </svg>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSelectedUser(null);
                                        setConversation([]);
                                        setTenantInfo(null);
                                    }}
                                    className="text-gray-400 hover:text-gray-500"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50">
                            {loadingConversation ? (
                                <div className="flex justify-center py-12">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
                                </div>
                            ) : conversation.length === 0 ? (
                                <p className="text-center text-gray-500 py-12">No conversation history found.</p>
                            ) : (
                                conversation.map((msg, index) => {
                                    // Check if this message should be highlighted
                                    const isHighlighted = highlightedMessageId &&
                                        (msg.id === highlightedMessageId || msg._id === highlightedMessageId);

                                    return (
                                        <div
                                            key={index}
                                            ref={isHighlighted ? highlightedMessageRef : null}
                                            className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                                        >
                                            <div className={`max-w-[80%] rounded-lg p-4 ${isHighlighted
                                                ? 'ring-4 ring-yellow-400 ring-opacity-50 shadow-lg'
                                                : ''
                                                } ${msg.sender === 'user'
                                                    ? `bg-black text-white rounded-br-none ${isHighlighted ? 'bg-opacity-90' : ''}`
                                                    : `bg-white border border-gray-200 text-gray-900 rounded-bl-none ${isHighlighted ? 'bg-yellow-50 border-yellow-300' : ''}`
                                                }`}>
                                                {isHighlighted && (
                                                    <div className="mb-2 flex items-center space-x-1 text-xs font-medium text-yellow-600">
                                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                                        </svg>
                                                        <span>Message with feedback</span>
                                                    </div>
                                                )}
                                                <div className="text-sm mb-1 opacity-75 flex justify-between gap-4">
                                                    <span className="capitalize">{msg.sender}</span>
                                                    <span>{formatDate(msg.timestamp)}</span>
                                                </div>
                                                <div className={`markdown-content ${msg.sender === 'user' ? 'prose-invert text-white' : 'text-gray-900'}`}>
                                                    <ReactMarkdown
                                                        remarkPlugins={[remarkGfm]}
                                                        components={{
                                                            h1: ({ node, children, ...props }) => <h1 className="text-xl font-bold mb-2 mt-4" {...props}>{children}</h1>,
                                                            h2: ({ node, children, ...props }) => <h2 className="text-lg font-bold mb-2 mt-3" {...props}>{children}</h2>,
                                                            h3: ({ node, children, ...props }) => <h3 className="text-md font-bold mb-1 mt-2" {...props}>{children}</h3>,
                                                            p: ({ node, children, ...props }) => <p className="mb-2 whitespace-pre-wrap text-sm leading-relaxed" {...props}>{children}</p>,
                                                            ul: ({ node, children, ...props }) => <ul className="list-disc ml-4 mb-2 space-y-1 text-sm" {...props}>{children}</ul>,
                                                            ol: ({ node, children, ...props }) => <ol className="list-decimal ml-4 mb-2 space-y-1 text-sm" {...props}>{children}</ol>,
                                                            li: ({ node, children, ...props }) => <li className="mb-1" {...props}>{children}</li>,
                                                            strong: ({ node, children, ...props }) => <strong className="font-bold text-inherit" {...props}>{children}</strong>,
                                                            code: ({ node, children, ...props }) => <code className="bg-gray-800 text-pink-400 px-1 rounded text-xs" {...props}>{children}</code>,
                                                        }}
                                                    >
                                                        {msg.message}
                                                    </ReactMarkdown>
                                                </div>
                                                {msg.mode && (
                                                    <div className={`mt-2 pt-2 text-xs opacity-75 ${msg.sender === 'user' ? 'border-t border-white/20' : 'border-t border-gray-200'
                                                        }`}>
                                                        Mode: {msg.mode}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AnalyticsDetails;
